import React, { useEffect } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  fetchDeviceConfig,
  updateDeviceConfig,
  setLocalField,
  clearDeviceConfig,
} from "./deviceConfigSlice";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../components/common/DangerConfirmField";

/**
 * Inline config panel for a device — shows the c1..c40 grid for an IMEI.
 * Mirrors the legacy `ConfigPanel.js` from the old project but in MUI.
 *
 * - Each cell is two inputs (Name + Value)
 * - c40 is the auto-incremented "Config Version" — read-only
 * - Save button gated by the security code (DangerConfirmField, "65432")
 *   so the destructive MQTT publish can't happen by accident
 * - Save publishes the flattened payload to the device via MQTT, then
 *   re-fetches the saved version from the API
 */
export default function ConfigPanel({ imei, onClose }) {
  const dispatch = useDispatch();
  const { imei: loadedImei, data, version, updatedAt, loading, saving } =
    useSelector((s) => s.deviceConfig);
  const [confirm, setConfirm] = React.useState("");

  useEffect(() => {
    if (imei) dispatch(fetchDeviceConfig(imei));
    return () => dispatch(clearDeviceConfig());
  }, [imei, dispatch]);

  const onChangeField = (key, field, value) =>
    dispatch(setLocalField({ key, field, value }));

  const onRefresh = () => dispatch(fetchDeviceConfig(imei));

  const onSave = async () => {
    if (!data) return;
    if (!isDangerCodeValid(confirm)) return;
    const res = await dispatch(updateDeviceConfig({ imei, data }));
    if (updateDeviceConfig.fulfilled.match(res)) {
      const newVersion = res.payload?.version ?? "?";
      const published = res.payload?.published;
      const publishError = res.payload?.publishError;
      if (published) {
        toast.success(`Config v${newVersion} saved & sent to device`);
      } else if (publishError) {
        toast.success(
          `Config v${newVersion} saved (MQTT: ${publishError})`,
          { icon: "⚠️" }
        );
      } else {
        toast.success(`Config v${newVersion} saved`);
      }
      setConfirm("");
    } else {
      toast.error(res.payload || "Failed to save");
    }
  };

  const isLoaded = data && loadedImei === imei;
  const keys = isLoaded
    ? Object.keys(data).sort(
        (a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10)
      )
    : [];

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        borderColor: "primary.main",
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background:
            "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
          color: "common.white",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            backgroundColor: "rgba(255,255,255,0.22)",
            border: "2px solid rgba(255,255,255,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <SettingsIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9375rem",
              letterSpacing: "-0.01em",
            }}
          >
            Device Configuration
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 0.25, flexWrap: "wrap" }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontFamily: "monospace",
                opacity: 0.92,
              }}
            >
              {imei}
            </Typography>
            {version != null && (
              <Chip
                label={`v${version}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  backgroundColor: "rgba(255,255,255,0.22)",
                  color: "common.white",
                  border: "1px solid rgba(255,255,255,0.35)",
                }}
              />
            )}
            {updatedAt && (
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  opacity: 0.85,
                  fontFamily: "monospace",
                }}
              >
                {dayjs(updatedAt).format("DD-MM-YYYY hh:mm A")}
              </Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title="Reload from server">
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={loading || saving}
              size="small"
              sx={{
                color: "common.white",
                backgroundColor: "rgba(255,255,255,0.15)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "common.white",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <CircularProgress size={22} />
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", mt: 1 }}
          >
            Loading configuration (c1–c40)…
          </Typography>
        </Box>
      ) : !isLoaded ? null : (
        <>
          <Box
            sx={{
              p: 1.5,
              display: "grid",
              gap: 1,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
            }}
          >
            {keys.map((key) => {
              const isVersion = key === "c40";
              return (
                <Paper
                  key={key}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: isVersion
                      ? "primary.light"
                      : "background.paper",
                    borderColor: isVersion ? "primary.main" : "divider",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box
                      sx={{
                        width: 36,
                        height: 22,
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isVersion
                          ? "primary.main"
                          : "action.hover",
                        color: isVersion ? "common.white" : "text.secondary",
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        fontFamily: "monospace",
                        flexShrink: 0,
                      }}
                    >
                      {key.toUpperCase()}
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Field name"
                      value={data[key]?.name || ""}
                      onChange={(e) =>
                        onChangeField(key, "name", e.target.value)
                      }
                      disabled={isVersion}
                      inputProps={{ "data-no-uppercase": "true" }}
                      InputProps={{
                        // Show the slot key (c1..c40) inline so the admin
                        // always sees which slot they're editing — handy
                        // when the field name has been customized away
                        // from its default label.
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                color: "text.disabled",
                              }}
                            >
                              ({key})
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flex: 1,
                        "& .MuiInputBase-input": {
                          fontSize: "0.75rem",
                          py: 0.5,
                        },
                      }}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Value"
                    value={data[key]?.value || ""}
                    onChange={(e) =>
                      onChangeField(key, "value", e.target.value)
                    }
                    disabled={isVersion}
                    inputProps={{ "data-no-uppercase": "true" }}
                    sx={{
                      mt: 0.75,
                      "& .MuiInputBase-input": {
                        fontSize: "0.8125rem",
                        fontFamily: "monospace",
                        fontWeight: 500,
                        py: 0.5,
                      },
                    }}
                  />
                </Paper>
              );
            })}
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.default",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Saving will publish the new values to the device via MQTT and
              bump the config version automatically.
            </Typography>
            <DangerConfirmField
              value={confirm}
              onChange={setConfirm}
              disabled={saving}
              label="Security code to save"
            />
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ mt: 1.25 }}
            >
              <Button
                onClick={onClose}
                variant="outlined"
                disabled={saving}
                sx={{ borderRadius: "8px" }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                variant="contained"
                color="primary"
                disabled={saving || !isDangerCodeValid(confirm)}
                startIcon={
                  saving ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )
                }
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
                }}
              >
                {saving ? "Saving…" : "Save & Publish"}
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Paper>
  );
}
